import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ModalBackdropService {

    private renderer: Renderer2;
    private backdropEl: HTMLElement | null = null;
    private refCount = 0;
    private unlisten: (() => void) | null = null;
    private hideTimer: ReturnType<typeof setTimeout> | null = null;
    private locked = false;
    private lockTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /** Lock backdrop visible during modal switching */
    lock(): void {
        this.locked = true;
        // Cancel any pending hide
        if (this.hideTimer) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }

        if (this.lockTimer) clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => {
            this.locked = false;
            this.lockTimer = null;
        }, 3000);
    }

    /** Unlock after navigation completes */
    unlock(): void {
        this.locked = false;
        if (this.lockTimer) {
            clearTimeout(this.lockTimer);
            this.lockTimer = null;
        }
    }

    show(
        dismissCallback: (() => void) | null,
        animationDuration: number
    ): void {
        this.refCount++;

        // Cancel any pending hide
        if (this.hideTimer) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }

        if (!this.backdropEl) {
            this.backdropEl = this.renderer.createElement('div');
            this.renderer.addClass(this.backdropEl, 'modal-backdrop');
            this.renderer.setStyle(this.backdropEl, 'z-index', '1000');
            this.renderer.setStyle(this.backdropEl,
                'transition', `opacity ${animationDuration}ms ease`
            );
            this.renderer.appendChild(document.body, this.backdropEl);
        }

        this.renderer.setStyle(this.backdropEl, 'display', 'block');
        void this.backdropEl!.offsetHeight;
        this.renderer.addClass(this.backdropEl, 'modal-backdrop--visible');
        this.renderer.setStyle(document.body, 'overflow', 'hidden');

        this.unlisten?.();
        this.unlisten = this.renderer.listen(
            this.backdropEl,
            'click',
            dismissCallback
                ? dismissCallback
                : (e: Event) => e.stopPropagation()
        );
    }

    hide(animationDuration: number): void {
        this.refCount = Math.max(0, this.refCount - 1);

        // Don't hide if locked during navigation
        if (this.locked) {
            return;
        }

        if (this.refCount > 0 || !this.backdropEl) return;

        this.unlisten?.();
        this.unlisten = null;

        this.renderer.removeClass(this.backdropEl, 'modal-backdrop--visible');

        if (this.hideTimer) clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => {
            if (this.backdropEl) {
                this.renderer.setStyle(this.backdropEl, 'display', 'none');
            }
            this.renderer.removeStyle(document.body, 'overflow');
            this.hideTimer = null;
        }, animationDuration);
    }
}
